import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useCreateCustomer, useEditCustomer } from "hooks/useCustomer"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { areAllIdsNumbers } from "helpers/object_helpers"

const CustomerAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const {
    isLoading: optionLoading,
    raw: rawData,
    refetch: refetchOptions,
  } = useFilteredVehicleOptions()
  const customerData = rawData?.customer ?? {}

  const designations = useMemo(
    () => customerData?.designations ?? [],
    [customerData?.designations]
  )
  const departments = useMemo(
    () => customerData?.departments ?? [],
    [customerData?.departments]
  )
  const companies = useMemo(
    () => customerData?.companies ?? [],
    [customerData?.companies]
  )
  const customerTypes = useMemo(
    () => customerData?.customer_types ?? [],
    [customerData?.customer_types]
  )
  const areas = useMemo(
    () => customerData?.customer_areas ?? [],
    [customerData?.customer_areas]
  )
  const cities = useMemo(
    () => customerData?.customer_city ?? [],
    [customerData?.customer_city]
  )

  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateCustomer()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditCustomer()

  const validationSchema = Yup.object({
    name: Yup.string().required("Please enter customer name"),
    mobile: Yup.string().max(20, "Mobile must be at most 20 characters"),
    email: Yup.string().email("Invalid email format").max(255),
    designation_id: Yup.string().nullable(),
    department_id: Yup.string().nullable(),
    company_id: Yup.string().nullable(),
    customer_type_id: Yup.string().nullable(),
    area_id: Yup.string().nullable(),
    city_id: Yup.string().nullable(),
    full_address: Yup.string().max(1000),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const isOptionRefetch = areAllIdsNumbers(values)

      const action = isEdit
        ? editMutation({ id: editedData.cuid, ...values })
        : createMutation(values)
      try {
        const res = await action
        
        if (!isOptionRefetch) {
          refetchOptions()
        }
        toast.success(
          res?.message ??
            `Customer ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save Customer.")
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      mobile: editedData?.mobile || "",
      email: editedData?.email || "",
      designation_id: editedData?.designation_id || "",
      department_id: editedData?.department_id || "",
      company_id: editedData?.company_id || "",
      customer_type_id: editedData?.customer_type_id || "",
      area_id: editedData?.area_id || "",
      city_id: editedData?.city_id || "",
      full_address: editedData?.full_address || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Customer"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => editLoading || createLoading,
    [editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Customer" : "Add Customer"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {/* Name */}
        <div className="mb-3">
          <Label htmlFor="name">
            Name <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        {/* Mobile */}
        <div className="mb-3">
          <Label htmlFor="mobile">Mobile</Label>
          <InputField
            type="text"
            name="mobile"
            id="mobile"
            value={formik.values.mobile}
            onChange={formik.handleChange}
            invalid={formik.touched.mobile && !!formik.errors.mobile}
          />
          {formik.touched.mobile && formik.errors.mobile && (
            <FormFeedback type="invalid">{formik.errors.mobile}</FormFeedback>
          )}
        </div>

        {/* Email */}
        <div className="mb-3">
          <Label htmlFor="email">Email</Label>
          <InputField
            type="email"
            name="email"
            id="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            invalid={formik.touched.email && !!formik.errors.email}
          />
          {formik.touched.email && formik.errors.email && (
            <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
          )}
        </div>

        {/* Full Address */}
        <div className="mb-3">
          <Label htmlFor="full_address">Full Address</Label>
          <InputField
            type="textarea"
            name="full_address"
            id="full_address"
            value={formik.values.full_address}
            onChange={formik.handleChange}
            invalid={
              formik.touched.full_address && !!formik.errors.full_address
            }
          />
          {formik.touched.full_address && formik.errors.full_address && (
            <FormFeedback type="invalid">
              {formik.errors.full_address}
            </FormFeedback>
          )}
        </div>

        <div className="mb-3 border-bottom">Additional Info</div>

        {/* Dropdowns (FormikCreatableSelect with labels) */}
        <FormikCreatableSelect
          name="designation_id"
          label="Designation"
          formik={formik}
          options={designations.map(d => ({ value: d.id, label: d.name }))}
          placeholder="Select or type a designation"
          loading={optionLoading}
        />

        <FormikCreatableSelect
          name="department_id"
          label="Department"
          formik={formik}
          options={departments.map(d => ({ value: d.id, label: d.name }))}
          placeholder="Select or type a department"
          loading={optionLoading}
        />

        <FormikCreatableSelect
          name="company_id"
          label="Company"
          formik={formik}
          options={companies.map(c => ({ value: c.id, label: c.name }))}
          placeholder="Select or type a company"
          loading={optionLoading}
        />

        <FormikCreatableSelect
          name="customer_type_id"
          label="Customer Type"
          formik={formik}
          options={customerTypes.map(ct => ({
            value: ct.id,
            label: ct.name,
          }))}
          placeholder="Select or type a customer type"
          loading={optionLoading}
        />

        <FormikCreatableSelect
          name="area_id"
          label="Area"
          formik={formik}
          options={areas.map(a => ({ value: a.id, label: a.name }))}
          placeholder="Select or type an area"
          loading={optionLoading}
        />

        <FormikCreatableSelect
          name="city_id"
          label="City"
          formik={formik}
          options={cities.map(c => ({ value: c.id, label: c.name }))}
          placeholder="Select or type a city"
          loading={optionLoading}
        />

        {/* Submit Button */}
        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default CustomerAddEdit
